import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import type { ShipmentUpdate, EmailSource } from "../shipments/ShipmentDetails";
import type {
  ShipmentApi,
  ListShipmentsApiRequest,
  ListShipmentsApiResponse,
  ManualUpdateShipmentRequest,
  ManualUpdateShipmentResponse,
  ReferenceNames,
  ShipmentAssignee,
  ListAssigneesRequest,
  ListAssigneesResponse,
  CreateAssigneeRequest,
  CreateAssigneeResponse,
  AssignShipmentRequest,
  AssignShipmentResponse,
  UnassignShipmentRequest,
  UnassignShipmentResponse,
  CreateShipmentRequest,
  CreateShipmentResponse,
} from "../../../src/shipments/types";
import type { ReminderWithUser } from "../../../src/shipments/reminders/types";
import type { ReminderType } from "../../../src/shipments/reminders/types";
import { apiClient } from "../utils/apiClient";

// Extended ShipmentApi interface for UI purposes
export interface ShipmentUI
  extends Omit<ShipmentApi, "createdAt" | "updatedAt"> {
  isTemporary?: boolean; // Flag for temporary unsaved rows
  createdAt: string | Date; // Ensure ISO string format
  updatedAt: string | Date; // Ensure ISO string format
  reminders?: ReminderWithUser[]; // Full reminder data (from /get-by-id)
}

interface EmailDialogState {
  isOpen: boolean;
  emailId: string | null;
  eventName: string;
}

interface SidebarState {
  isOpen: boolean;
  shipmentId: string | null;
  updates?: ShipmentUpdate[];
  emailSource?: EmailSource;
}

interface ShipmentsData {
  shipmentData: Record<string, ShipmentUI>;
  shipmentList: string[];
  temporaryShipmentEdits: {
    editsMap: Record<string, Partial<ShipmentUI>>; // The actual property edits
    activeEdit: {
      tempId: string;
      columnField: string;
      cursorPosition: number; // Current cursor position in the input
      selectAll: boolean; // Whether to select all text (true on initial click, false when restoring)
    } | null;
  };
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
}

interface AssigneesData {
  assigneeData: Record<string, ShipmentAssignee>;
  assigneeList: string[];
  loading: boolean;
  error: string | null;
}

interface ExceptionsData {
  reminders: ReminderWithUser[];
  loading: boolean;
  error: string | null;
  total: number;
}

interface ShipmentState {
  emailDialog: EmailDialogState;
  sidebar: SidebarState;
  shipments: ShipmentsData;
  assignees: AssigneesData;
  exceptions: ExceptionsData;
}

const initialState: ShipmentState = {
  emailDialog: {
    isOpen: false,
    emailId: null,
    eventName: "",
  },
  sidebar: {
    isOpen: false,
    shipmentId: null,
    updates: undefined,
    emailSource: undefined,
  },
  shipments: {
    shipmentData: {},
    shipmentList: [],
    temporaryShipmentEdits: {
      editsMap: {},
      activeEdit: null,
    },
    loading: false,
    error: null,
    hasMore: false,
    totalCount: 0,
  },
  assignees: {
    assigneeData: {},
    assigneeList: [],
    loading: false,
    error: null,
  },
  exceptions: {
    reminders: [],
    loading: false,
    error: null,
    total: 0,
  },
};

// Async thunk for fetching shipments
interface FetchShipmentsParams {
  offset?: number;
  search?: string;
  append?: boolean;
  tags?: string[];
  assigneeIds?: string[];
  showUnassignedOnly?: boolean;
}

export const fetchShipments = createAsyncThunk<
  {
    shipments: ShipmentApi[];
    hasMore: boolean;
    total: number;
    append: boolean;
  },
  FetchShipmentsParams
>(
  "shipment/fetchShipments",
  async ({
    offset = 0,
    search,
    append = false,
    tags = [],
    assigneeIds = [],
    showUnassignedOnly = false,
  }) => {
    const requestBody: ListShipmentsApiRequest = {
      offset,
      tags: tags.length > 0 ? tags : undefined,
      search: search || undefined,
      includeInvalid: false,
      includeMerged: false,
      sortBy: "last_update",
      assigneeIds: assigneeIds.length > 0 ? assigneeIds : undefined,
      showUnassignedOnly,
    };

    const response = await apiClient.post<ListShipmentsApiResponse>(
      "/shipments/list",
      requestBody
    );

    return {
      shipments: response.shipments,
      hasMore: response.hasMore,
      total: response.total || 0,
      append,
    };
  }
);

// Fields that allow multiple comma/space/slash separated values
const MULTI_VALUE_FIELDS: ReferenceNames[] = ["container_number"];

// Helper function to split multi-value fields by common separators
const splitMultiValue = (value: string): string[] => {
  return value
    .split(/[,/\\\s]+/) // Split by comma, forward/back slash, or space
    .map((v) => v.trim())
    .filter(Boolean); // Remove empty strings
};

// Async thunk for updating shipment property
interface UpdateShipmentPropertyParams {
  shipmentId: string;
  attribute: ReferenceNames;
  newValue: string;
}

export const updateShipmentProperty = createAsyncThunk<
  { shipmentId: string; updatedShipment: ShipmentApi },
  UpdateShipmentPropertyParams,
  { rejectValue: string }
>(
  "shipment/updateShipmentProperty",
  async ({ shipmentId, attribute, newValue }, { rejectWithValue }) => {
    try {
      // Determine if this is a multi-value field
      const isMultiValue = MULTI_VALUE_FIELDS.includes(attribute);

      // Split value if it's a multi-value field
      const newValues = isMultiValue ? splitMultiValue(newValue) : [newValue];

      // Prepare the update request
      const requestBody: ManualUpdateShipmentRequest = {
        shipmentId,
        updateOperation: {
          addedProperties: [],
          modifiedProperties: [],
          replaceProperties: [
            {
              attribute,
              newValues,
            },
          ],
        },
      };

      // Call the API
      const response = await apiClient.post<ManualUpdateShipmentResponse>(
        "/shipments/update",
        requestBody
      );

      // Check if the update failed
      if (response.status === "failed") {
        return rejectWithValue(response.message || "Update failed");
      }

      // Fetch the updated shipment data
      const updatedShipment = await apiClient.post<ShipmentApi>(
        "/shipments/get-by-id",
        { shipmentId }
      );

      return { shipmentId, updatedShipment };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update shipment");
    }
  }
);

// Async thunk for fetching assignees
interface FetchAssigneesParams {
  includeOrgUsers?: boolean;
}

export const fetchAssignees = createAsyncThunk<
  ShipmentAssignee[],
  FetchAssigneesParams
>("shipment/fetchAssignees", async ({ includeOrgUsers = true }) => {
  const requestBody: ListAssigneesRequest = {
    includeOrgUsers,
  };

  const response = await apiClient.post<ListAssigneesResponse>(
    "/shipments/assignee/list",
    requestBody
  );

  return response.assignees;
});

// Async thunk for creating an assignee
interface CreateAssigneeParams {
  userId?: string;
  userEmail?: string;
  name?: string;
  type: "dobby_user" | "email" | "name";
}

export const createAssignee = createAsyncThunk<
  ShipmentAssignee,
  CreateAssigneeParams,
  { rejectValue: string }
>(
  "shipment/createAssignee",
  async ({ userId, userEmail, name, type }, { rejectWithValue }) => {
    try {
      const requestBody: CreateAssigneeRequest = {
        userId,
        userEmail,
        name,
        type,
      };

      const response = await apiClient.post<CreateAssigneeResponse>(
        "/shipments/assignee/create",
        requestBody
      );

      if (response.status === "failed" || !response.assignee) {
        return rejectWithValue(response.message || "Assignee creation failed");
      }

      return response.assignee;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create assignee");
    }
  }
);

// Async thunk for assigning a shipment to a user
interface AssignShipmentParams {
  shipmentId: string;
  assigneeId?: string;
  userId?: string;
  userEmail?: string;
  name?: string;
}

export const assignShipment = createAsyncThunk<
  { shipmentId: string; assignee: ShipmentAssignee },
  AssignShipmentParams,
  { rejectValue: string }
>(
  "shipment/assignShipment",
  async (
    { shipmentId, assigneeId, userId, userEmail, name },
    { rejectWithValue }
  ) => {
    try {
      const requestBody: AssignShipmentRequest = {
        shipmentId,
        assigneeId,
        userId,
        userEmail,
        name,
      };

      const response = await apiClient.post<AssignShipmentResponse>(
        "/shipments/assignee/assign",
        requestBody
      );

      if (response.status === "failed" || !response.assignee) {
        return rejectWithValue(response.message || "Assignment failed");
      }

      return { shipmentId, assignee: response.assignee };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to assign shipment");
    }
  }
);

// Async thunk for unassigning a shipment from a user
interface UnassignShipmentParams {
  shipmentId: string;
  assigneeId: string;
}

export const unassignShipment = createAsyncThunk<
  { shipmentId: string; assigneeId: string },
  UnassignShipmentParams,
  { rejectValue: string }
>(
  "shipment/unassignShipment",
  async ({ shipmentId, assigneeId }, { rejectWithValue }) => {
    try {
      const requestBody: UnassignShipmentRequest = {
        shipmentId,
        assigneeId,
      };

      const response = await apiClient.post<UnassignShipmentResponse>(
        "/shipments/assignee/unassign",
        requestBody
      );

      if (response.status === "failed") {
        return rejectWithValue(response.message || "Unassignment failed");
      }

      return { shipmentId, assigneeId };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to unassign shipment");
    }
  }
);

// Async thunk for creating a new shipment
interface CreateShipmentParams {
  tempId: string;
  tags: string[];
  properties: Array<{
    name: ReferenceNames;
    value: string;
    status: "final" | "pending";
  }>;
  assignedToIds: string[];
}

export const createShipment = createAsyncThunk<
  { tempId: string; shipment: ShipmentApi },
  CreateShipmentParams,
  { rejectValue: string }
>(
  "shipment/createShipment",
  async ({ tempId, tags, properties, assignedToIds }, { rejectWithValue }) => {
    try {
      // Filter out empty properties before sending
      const filteredProperties = properties.filter(
        (p) => p.value.trim() !== ""
      );

      const requestBody: CreateShipmentRequest = {
        tags,
        properties: filteredProperties,
        assignedToIds,
      };

      const response = await apiClient.post<CreateShipmentResponse>(
        "/shipments/create",
        requestBody
      );

      if (response.status === "failed" || !response.shipment) {
        return rejectWithValue(response.message || "Creation failed");
      }

      return { tempId, shipment: response.shipment };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create shipment");
    }
  }
);

// Async thunk for saving a temporary shipment (reads edits from state)
export const saveTemporaryShipment = createAsyncThunk<
  { tempId: string; shipment: ShipmentApi },
  { tempId: string },
  { rejectValue: string; state: { shipment: ShipmentState } }
>(
  "shipment/saveTemporaryShipment",
  async ({ tempId }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const tempShipment = state.shipment.shipments.shipmentData[tempId];

      if (!tempShipment || !tempShipment.isTemporary) {
        return rejectWithValue("Temporary shipment not found");
      }

      // Merge temporary edits with base shipment data
      const edits =
        state.shipment.shipments.temporaryShipmentEdits.editsMap[tempId];
      const properties = edits?.properties || tempShipment.properties;

      // Filter out empty properties before sending
      const filteredProperties = properties.filter(
        (p) => p.value.trim() !== ""
      );

      const requestBody: CreateShipmentRequest = {
        tags: tempShipment.tags,
        properties: filteredProperties,
        assignedToIds: tempShipment.assignedToIds || [],
      };

      const response = await apiClient.post<CreateShipmentResponse>(
        "/shipments/create",
        requestBody
      );

      if (response.status === "failed" || !response.shipment) {
        return rejectWithValue(response.message || "Creation failed");
      }

      return { tempId, shipment: response.shipment };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create shipment");
    }
  }
);

// Async thunk for fetching exceptions (reminders)
interface FetchExceptionsParams {
  orgId?: string;
  userIds?: string[];
  types?: ReminderType[];
  fromDate?: string;
  toDate?: string;
  offset?: number;
  limit?: number;
}

export const fetchExceptions = createAsyncThunk<
  {
    reminders: ReminderWithUser[];
    total: number;
  },
  FetchExceptionsParams
>(
  "shipment/fetchExceptions",
  async ({
    orgId,
    userIds,
    types,
    fromDate,
    toDate,
    offset = 0,
    limit = 100,
  }) => {
    const requestBody: any = {
      offset,
      limit,
    };

    if (orgId) requestBody.orgId = orgId;
    if (userIds && userIds.length > 0) requestBody.userIds = userIds;
    if (types && types.length > 0) requestBody.types = types;
    if (fromDate) requestBody.fromDate = fromDate;
    if (toDate) requestBody.toDate = toDate;

    const response = await apiClient.post<{
      reminders: ReminderWithUser[];
      total: number;
      offset: number;
      limit: number;
    }>("/shipments/reminders/list-reminders", requestBody);

    return {
      reminders: response.reminders,
      total: response.total,
    };
  }
);

interface OpenEmailPopupPayload {
  emailId: string;
  eventName: string;
}

interface OpenShipmentSidebarPayload {
  shipmentId: string;
  updates?: ShipmentUpdate[];
  emailSource?: EmailSource;
}

const shipmentSlice = createSlice({
  name: "shipment",
  initialState,
  reducers: {
    openEmailPopup: (state, action: PayloadAction<OpenEmailPopupPayload>) => {
      state.emailDialog.isOpen = true;
      state.emailDialog.emailId = action.payload.emailId;
      state.emailDialog.eventName = action.payload.eventName;
    },
    closeEmailPopup: (state) => {
      state.emailDialog.isOpen = false;
      state.emailDialog.emailId = null;
      state.emailDialog.eventName = "";
    },
    openShipmentSidebar: (
      state,
      action: PayloadAction<OpenShipmentSidebarPayload>
    ) => {
      state.sidebar.isOpen = true;
      state.sidebar.shipmentId = action.payload.shipmentId;
      state.sidebar.updates = action.payload.updates;
      state.sidebar.emailSource = action.payload.emailSource;
    },
    closeShipmentSidebar: (state) => {
      state.sidebar.isOpen = false;
      state.sidebar.shipmentId = null;
      state.sidebar.updates = undefined;
      state.sidebar.emailSource = undefined;
    },
    setActiveEdit: (
      state,
      action: PayloadAction<{
        tempId: string;
        columnField: string;
        cursorPosition: number;
        selectAll: boolean;
      } | null>
    ) => {
      state.shipments.temporaryShipmentEdits.activeEdit = action.payload;
    },
    createTemporaryShipment: (
      state,
      action: PayloadAction<{ tags: string[] }>
    ) => {
      // Generate temporary ID
      const tempId = `temp-${Date.now()}`;

      // Create temporary shipment with empty properties
      const tempShipment: ShipmentUI = {
        id: tempId,
        name: "",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        tags: action.payload.tags,
        properties: [], // Start with empty array
        assignedToIds: [],
        totalReminders: 0,
        unresolvedExceptions: 0,
        unresolvedReminders: 0,
        unresolvedMilestones: 0,
        isTemporary: true,
      };

      // Add to state
      state.shipments.shipmentData[tempId] = tempShipment;
      // Prepend to list (show at top)
      state.shipments.shipmentList.unshift(tempId);
    },
    discardTemporaryShipment: (
      state,
      action: PayloadAction<{ tempId: string }>
    ) => {
      const { tempId } = action.payload;

      // Remove from shipmentData
      delete state.shipments.shipmentData[tempId];

      // Remove from shipmentList
      state.shipments.shipmentList = state.shipments.shipmentList.filter(
        (id) => id !== tempId
      );

      // Remove from temporary edits
      delete state.shipments.temporaryShipmentEdits.editsMap[tempId];

      // Clear active edit if it matches this shipment
      if (
        state.shipments.temporaryShipmentEdits.activeEdit?.tempId === tempId
      ) {
        state.shipments.temporaryShipmentEdits.activeEdit = null;
      }
    },
    // temporary edits, while typing for example
    updateTemporaryShipmentEdit: (
      state,
      action: PayloadAction<{
        tempId: string;
        attribute: ReferenceNames;
        value: string;
        cursorPosition: number;
      }>
    ) => {
      const { tempId, attribute, value, cursorPosition } = action.payload;
      const shipment = state.shipments.shipmentData[tempId];

      if (!shipment || !shipment.isTemporary) {
        return;
      }

      // Initialize edits for this temp shipment if not exists
      if (!state.shipments.temporaryShipmentEdits.editsMap[tempId]) {
        state.shipments.temporaryShipmentEdits.editsMap[tempId] = {
          properties: [],
        };
      }

      const edits = state.shipments.temporaryShipmentEdits.editsMap[tempId];

      // Ensure properties array exists
      if (!edits.properties) {
        edits.properties = [];
      }

      // Find existing property in edits
      const existingProperty = edits.properties.find(
        (prop: any) => prop.name === attribute
      );

      // Update or add property in edits map
      if (existingProperty) {
        existingProperty.value = value;
      } else if (value.trim() !== "") {
        edits.properties.push({
          name: attribute,
          value: value,
          status: "final",
        });
      }

      // Update cursor position in activeEdit if it exists
      // BUT preserve the selectAll flag (don't change it during typing)
      if (state.shipments.temporaryShipmentEdits.activeEdit) {
        state.shipments.temporaryShipmentEdits.activeEdit.cursorPosition =
          cursorPosition;
        // After first keystroke, we're no longer "selecting all"
        state.shipments.temporaryShipmentEdits.activeEdit.selectAll = false;
      }
    },
    updateTemporaryShipmentProperty: (
      state,
      action: PayloadAction<{
        tempId: string;
        attribute: ReferenceNames;
        value: string;
      }>
    ) => {
      const { tempId, attribute, value } = action.payload;
      const shipment = state.shipments.shipmentData[tempId];

      if (!shipment || !shipment.isTemporary) return;

      // Update the property directly in the shipment
      const existingProperty = shipment.properties.find(
        (prop) => prop.name === attribute
      );

      if (existingProperty) {
        existingProperty.value = value;
      } else if (value.trim() !== "") {
        shipment.properties.push({
          name: attribute,
          value: value,
          status: "final",
        });
      }
    },
    assignTemporaryShipment: (
      state,
      action: PayloadAction<{
        tempId: string;
        assigneeId: string;
      }>
    ) => {
      const { tempId, assigneeId } = action.payload;
      const shipment = state.shipments.shipmentData[tempId];

      if (!shipment || !shipment.isTemporary) return;

      // Add assignee if not already assigned
      if (!shipment.assignedToIds.includes(assigneeId)) {
        shipment.assignedToIds.push(assigneeId);
      }
    },
    unassignTemporaryShipment: (
      state,
      action: PayloadAction<{
        tempId: string;
        assigneeId: string;
      }>
    ) => {
      const { tempId, assigneeId } = action.payload;
      const shipment = state.shipments.shipmentData[tempId];

      if (!shipment || !shipment.isTemporary) return;

      // Remove assignee from assignedToIds
      shipment.assignedToIds = shipment.assignedToIds.filter(
        (id) => id !== assigneeId
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShipments.pending, (state, action) => {
        if (!action.meta.arg.append) {
          state.shipments.loading = true;
        }
        state.shipments.error = null;
      })
      .addCase(fetchShipments.fulfilled, (state, action) => {
        state.shipments.loading = false;
        const { shipments, hasMore, total, append } = action.payload;

        if (append) {
          // Append mode: add new shipments to existing ones
          shipments.forEach((shipment) => {
            if (!state.shipments.shipmentData[shipment.id]) {
              state.shipments.shipmentData[shipment.id] = shipment;
              state.shipments.shipmentList.push(shipment.id);
            }
          });
        } else {
          // Replace mode: clear and set new shipments
          state.shipments.shipmentData = {};
          state.shipments.shipmentList = [];
          shipments.forEach((shipment) => {
            state.shipments.shipmentData[shipment.id] = shipment;
            state.shipments.shipmentList.push(shipment.id);
          });
        }

        state.shipments.hasMore = hasMore;
        state.shipments.totalCount = total;
      })
      .addCase(fetchShipments.rejected, (state, action) => {
        state.shipments.loading = false;
        state.shipments.error =
          action.error.message || "Failed to load shipments";
      })
      // Optimistic update for shipment property
      .addCase(updateShipmentProperty.pending, (state, action) => {
        const { shipmentId, attribute, newValue } = action.meta.arg;
        const shipment = state.shipments.shipmentData[shipmentId];

        if (shipment) {
          // Store original shipment for potential rollback
          (state.shipments as any)._pendingUpdate = {
            shipmentId,
            originalShipment: { ...shipment },
          };

          // Optimistically update the property
          const isMultiValue = MULTI_VALUE_FIELDS.includes(attribute);
          const newValues = isMultiValue
            ? splitMultiValue(newValue)
            : [newValue];

          // Remove all existing properties with this attribute
          shipment.properties = shipment.properties.filter(
            (prop) => prop.name !== attribute
          );

          // Add new properties
          newValues.forEach((value) => {
            shipment.properties.push({
              name: attribute,
              value,
              status: "final",
            });
          });

          // Update the shipment in the store
          state.shipments.shipmentData[shipmentId] = shipment;
        }
      })
      .addCase(updateShipmentProperty.fulfilled, (state, action) => {
        const { shipmentId, updatedShipment } = action.payload;

        // Replace with the server's response
        state.shipments.shipmentData[shipmentId] = updatedShipment;

        // Clear the pending update
        delete (state.shipments as any)._pendingUpdate;
      })
      .addCase(updateShipmentProperty.rejected, (state, action) => {
        // Rollback to original shipment on failure
        const pendingUpdate = (state.shipments as any)._pendingUpdate;

        if (pendingUpdate) {
          const { shipmentId, originalShipment } = pendingUpdate;
          state.shipments.shipmentData[shipmentId] = originalShipment;
          delete (state.shipments as any)._pendingUpdate;
        }

        // Set error message
        state.shipments.error = action.payload || "Failed to update shipment";
      })
      // Fetch assignees
      .addCase(fetchAssignees.pending, (state) => {
        state.assignees.loading = true;
        state.assignees.error = null;
      })
      .addCase(fetchAssignees.fulfilled, (state, action) => {
        state.assignees.loading = false;
        const assignees = action.payload;

        // Clear and rebuild assignee data
        state.assignees.assigneeData = {};
        state.assignees.assigneeList = [];

        assignees.forEach((assignee) => {
          // Use a generated key for user-not-available assignees (id is null)
          const key =
            assignee.id || `not-available-${assignee.userId || Date.now()}`;
          state.assignees.assigneeData[key] = assignee;
          state.assignees.assigneeList.push(key);
        });
      })
      .addCase(fetchAssignees.rejected, (state, action) => {
        state.assignees.loading = false;
        state.assignees.error =
          action.error.message || "Failed to load assignees";
      })
      // Create assignee
      .addCase(createAssignee.pending, (state) => {
        state.assignees.loading = true;
        state.assignees.error = null;
      })
      .addCase(createAssignee.fulfilled, (state, action) => {
        state.assignees.loading = false;
        const assignee = action.payload;

        // If there was a user-not-available assignee, remove it first
        if (assignee.userId) {
          const virtualKey = Object.keys(state.assignees.assigneeData).find(
            (key) =>
              state.assignees.assigneeData[key].userId === assignee.userId &&
              state.assignees.assigneeData[key].assigneeUserNotAvailable
          );
          if (virtualKey) {
            delete state.assignees.assigneeData[virtualKey];
            state.assignees.assigneeList = state.assignees.assigneeList.filter(
              (id) => id !== virtualKey
            );
          }
        }

        // Add the real assignee to store if it has a valid ID and isn't already present
        if (assignee.id && !state.assignees.assigneeData[assignee.id]) {
          state.assignees.assigneeData[assignee.id] = assignee;
          state.assignees.assigneeList.push(assignee.id);
        }
      })
      .addCase(createAssignee.rejected, (state, action) => {
        state.assignees.loading = false;
        state.assignees.error = action.payload || "Failed to create assignee";
      })
      // Assign shipment (optimistic update)
      .addCase(assignShipment.pending, (state, action) => {
        const { shipmentId, assigneeId } = action.meta.arg;
        const shipment = state.shipments.shipmentData[shipmentId];

        if (shipment && assigneeId) {
          // Store original assignedToIds for potential rollback
          (state.assignees as any)._pendingAssign = {
            shipmentId,
            assigneeId,
            originalAssignedToIds: [...shipment.assignedToIds],
          };

          // Optimistically add assignee to shipment (if we know the assigneeId)
          if (!shipment.assignedToIds.includes(assigneeId)) {
            shipment.assignedToIds.push(assigneeId);
          }
        }
      })
      .addCase(assignShipment.fulfilled, (state, action) => {
        const { shipmentId, assignee } = action.payload;
        const shipment = state.shipments.shipmentData[shipmentId];

        // Add or update assignee in the store (handle nullable id)
        if (assignee.id) {
          if (!state.assignees.assigneeData[assignee.id]) {
            state.assignees.assigneeData[assignee.id] = assignee;
            state.assignees.assigneeList.push(assignee.id);
          } else {
            state.assignees.assigneeData[assignee.id] = assignee;
          }
        }

        // Update shipment's assignedToIds (only if assignee has a real ID)
        if (
          shipment &&
          assignee.id &&
          !shipment.assignedToIds.includes(assignee.id)
        ) {
          shipment.assignedToIds.push(assignee.id);
        }

        // Clear the pending assign
        delete (state.assignees as any)._pendingAssign;
      })
      .addCase(assignShipment.rejected, (state, action) => {
        // Rollback optimistic update
        const pendingAssign = (state.assignees as any)._pendingAssign;

        if (pendingAssign) {
          const { shipmentId, originalAssignedToIds } = pendingAssign;
          const shipment = state.shipments.shipmentData[shipmentId];

          if (shipment) {
            shipment.assignedToIds = originalAssignedToIds;
          }

          delete (state.assignees as any)._pendingAssign;
        }

        state.assignees.error = action.payload || "Failed to assign shipment";
      })
      // Unassign shipment (optimistic update)
      .addCase(unassignShipment.pending, (state, action) => {
        const { shipmentId, assigneeId } = action.meta.arg;
        const shipment = state.shipments.shipmentData[shipmentId];

        // Store original assignedToIds for potential rollback
        if (shipment) {
          (state.assignees as any)._pendingUnassign = {
            shipmentId,
            assigneeId,
            originalAssignedToIds: [...shipment.assignedToIds],
          };

          // Optimistically remove assignee from shipment
          shipment.assignedToIds = shipment.assignedToIds.filter(
            (id) => id !== assigneeId
          );
        }
      })
      .addCase(unassignShipment.fulfilled, (state, action) => {
        const { shipmentId, assigneeId } = action.payload;
        const shipment = state.shipments.shipmentData[shipmentId];

        // Ensure assignee is removed from shipment (in case optimistic update was skipped)
        if (shipment) {
          shipment.assignedToIds = shipment.assignedToIds.filter(
            (id) => id !== assigneeId
          );
        }

        // Clear the pending unassign
        delete (state.assignees as any)._pendingUnassign;

        // Keep the assignee in the store for future use
      })
      .addCase(unassignShipment.rejected, (state, action) => {
        // Rollback optimistic update
        const pendingUnassign = (state.assignees as any)._pendingUnassign;

        if (pendingUnassign) {
          const { shipmentId, originalAssignedToIds } = pendingUnassign;
          const shipment = state.shipments.shipmentData[shipmentId];

          if (shipment) {
            shipment.assignedToIds = originalAssignedToIds;
          }

          delete (state.assignees as any)._pendingUnassign;
        }

        state.assignees.error = action.payload || "Failed to unassign shipment";
      })
      // Create shipment
      .addCase(createShipment.pending, (state) => {
        state.shipments.loading = true;
        state.shipments.error = null;
      })
      .addCase(createShipment.fulfilled, (state, action) => {
        state.shipments.loading = false;
        const { tempId, shipment } = action.payload;

        // Remove temporary shipment
        delete state.shipments.shipmentData[tempId];
        state.shipments.shipmentList = state.shipments.shipmentList.filter(
          (id) => id !== tempId
        );

        // Remove temporary edits
        delete state.shipments.temporaryShipmentEdits.editsMap[tempId];

        // Clear active edit if it matches this shipment
        if (
          state.shipments.temporaryShipmentEdits.activeEdit?.tempId === tempId
        ) {
          state.shipments.temporaryShipmentEdits.activeEdit = null;
        }

        // Add real shipment at the top
        state.shipments.shipmentData[shipment.id] = shipment;
        state.shipments.shipmentList.unshift(shipment.id);
        state.shipments.totalCount += 1;
      })
      .addCase(createShipment.rejected, (state, action) => {
        state.shipments.loading = false;
        // Keep temporary shipment so user can try again or discard
        state.shipments.error = action.payload || "Failed to create shipment";
      })
      // Save temporary shipment (same logic as createShipment)
      .addCase(saveTemporaryShipment.pending, (state) => {
        state.shipments.loading = true;
        state.shipments.error = null;
      })
      .addCase(saveTemporaryShipment.fulfilled, (state, action) => {
        state.shipments.loading = false;
        const { tempId, shipment } = action.payload;

        // Remove temporary shipment
        delete state.shipments.shipmentData[tempId];
        state.shipments.shipmentList = state.shipments.shipmentList.filter(
          (id) => id !== tempId
        );

        // Remove temporary edits
        delete state.shipments.temporaryShipmentEdits.editsMap[tempId];

        // Clear active edit if it matches this shipment
        if (
          state.shipments.temporaryShipmentEdits.activeEdit?.tempId === tempId
        ) {
          state.shipments.temporaryShipmentEdits.activeEdit = null;
        }

        // Add real shipment at the top
        state.shipments.shipmentData[shipment.id] = shipment;
        state.shipments.shipmentList.unshift(shipment.id);
        state.shipments.totalCount += 1;
      })
      .addCase(saveTemporaryShipment.rejected, (state, action) => {
        state.shipments.loading = false;
        // Keep temporary shipment so user can try again or discard
        state.shipments.error = action.payload || "Failed to create shipment";
      })
      // Fetch exceptions
      .addCase(fetchExceptions.pending, (state) => {
        state.exceptions.loading = true;
        state.exceptions.error = null;
      })
      .addCase(fetchExceptions.fulfilled, (state, action) => {
        state.exceptions.loading = false;
        const { reminders, total } = action.payload;
        state.exceptions.reminders = reminders;
        state.exceptions.total = total;
      })
      .addCase(fetchExceptions.rejected, (state, action) => {
        state.exceptions.loading = false;
        state.exceptions.error =
          action.error.message || "Failed to load exceptions";
      });
  },
});

export const {
  openEmailPopup,
  closeEmailPopup,
  openShipmentSidebar,
  closeShipmentSidebar,
  setActiveEdit,
  createTemporaryShipment,
  discardTemporaryShipment,
  updateTemporaryShipmentEdit,
  updateTemporaryShipmentProperty,
  assignTemporaryShipment,
  unassignTemporaryShipment,
} = shipmentSlice.actions;

export default shipmentSlice.reducer;
